import { useEffect, useState } from "react";
import { reload } from "firebase/auth";
import useFirebaseAuthentication from "../../hooks/useFirebaseAuthentication";
import SignupStepIndicator from "../SignupStepIndicator";
import { sendVerificationEmailRequest } from "../../functions";

const EmailVerificationPrompt: React.FC = () => {
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { authUser } = useFirebaseAuthentication();

  useEffect(() => {
    if (!authUser || authUser.emailVerified) return;

    let isMounted = true;
    let reloadInProgress = false;

    const refreshVerificationStatus = async () => {
      if (reloadInProgress) return;

      reloadInProgress = true;
      try {
        await reload(authUser);
        if (isMounted && authUser.emailVerified) {
          window.location.reload();
        }
      } catch (err) {
        console.error("Error refreshing email verification status:", err);
      } finally {
        reloadInProgress = false;
      }
    };

    const intervalId = window.setInterval(() => {
      void refreshVerificationStatus();
    }, 3000);

    void refreshVerificationStatus();

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [authUser]);

  const handleResendEmail = async () => {
    if (!authUser) return;
    setLoading(true);
    setError("");
    try {
      await sendVerificationEmailRequest();
      setEmailSent(true);
    } catch (err) {
      console.error(err);
      setError("Failed to send verification email. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (!authUser || authUser.emailVerified) return null;

  return (
    <div className="mx-auto md:w-[30em] px-4 py-10">
      <SignupStepIndicator currentStep={3} />
      <div className="max-w-md mx-auto my-6 p-6 border border-gray-300 rounded-2xl bg-white shadow-md text-center">
        <h2 className="text-xl font-semibold mb-2">Verify Your Email</h2>
        <p className="text-gray-700 mb-4">
          Please check your inbox at{" "}
          <span className="font-medium">{authUser.email}</span> for a
          verification link to ensure your account is secure.
        </p>
        <p className="text-gray-500 text-sm mb-4">
          This page will update automatically once your email is verified.
        </p>

        <button
          onClick={handleResendEmail}
          disabled={loading}
          className={`px-4 py-2 rounded-lg text-white transition-colors ${
            loading ? "bg-slate-600 cursor-not-allowed" : "bg-slate-800"
          }`}
        >
          {loading ? "Sending..." : "Resend Verification Email"}
        </button>

        {emailSent && (
          <p className="text-green-600 font-medium mt-4">
            Verification email sent! Check your inbox.
          </p>
        )}

        {error && <p className="text-red-600 mt-4">{error}</p>}
      </div>
    </div>
  );
};

export default EmailVerificationPrompt;
